/**
 * Reusable type guard utilities for Storyblok content validation
 */
import type { ISbStoryData } from '@storyblok/vue'
import type { SelfAssessmentPageStory } from '~/components/self-assessment/SelfAssessmentPages.vue';
import type { StoryContentBlok } from '~/composables/useStoryContent'
import type { ContentTypeAuthorStoryblok } from '~/types/storyblok';

/**
 * Utility type to extract component literal types from a union
 */
type ExtractComponent<T> = T extends { component: infer C } ? C : never

/**
 * Extract all component types from StoryContentBlok union as literal types
 */
type ComponentType = ExtractComponent<StoryContentBlok>

/**
 * Generic type guard to check if a story has content with a specific component type
 */
export function isStoryOfType<T extends ComponentType>(
  story: any,
  componentType: T
): story is ISbStoryData<{ component: T; _uid: string }> {
  const content = story?.content
  return content &&
         content.component === componentType &&
         content._uid &&
         typeof content._uid === 'string'
}

/**
 * Type guard to check if story content has valid basic properties
 */
export function hasValidStoryContent(story: any): story is ISbStoryData<{ component: ComponentType; _uid: string }> {
  const content = story?.content
  return content &&
         typeof content.component === 'string' &&
         content._uid &&
         typeof content._uid === 'string'
}

/**
 * Type guard for stories with content matching any of the specified component types
 */
export function isStoryOfTypes<T extends ComponentType>(
  story: any,
  componentTypes: T[]
): story is ISbStoryData<{ component: T; _uid: string }> {
  const content = story?.content
  return content &&
         componentTypes.includes(content.component as T) &&
         content._uid &&
         typeof content._uid === 'string'
}

// --- Specific type guard shortcuts - add more as needed ---

export function isAuthorStory(story: any): story is ContentTypeAuthorStoryblok {
  return isStoryOfType(story, 'ContentTypeAuthor')
}

export function isSelfAssessmentStory(story: any): story is SelfAssessmentPageStory {
  return isStoryOfTypes(story, ['AudiencePage', 'FeaturedTopicPage', 'Page', 'Resource', 'LandingPage'])
}
